// ============================================================================
// REDUCER - iotReducer.js
// ============================================================================
// ./context/iot/iotReducer.js

const iotReducer = (state, { type, payload }) => {
    switch (type) {
        // Log Management
        case 'SET_LOGS':
            return {
                ...state,
                logs: payload,
                isLoading: false,
                error: null
            };
        
        case 'SET_CURRENT_LOG':
            return {
                ...state,
                currentLog: payload,
                isLoading: false,
                error: null
            };
        
        case 'ADD_LOG':
            return {
                ...state,
                logs: [payload, ...state.logs],
                isLoading: false,
                error: null
            };
        
        case 'UPDATE_LOG':
            return {
                ...state,
                logs: state.logs.map(log =>
                    log._id === payload._id ? payload : log
                ),
                currentLog: state.currentLog?._id === payload._id 
                    ? payload 
                    : state.currentLog,
                isLoading: false,
                error: null
            };
        
        case 'DELETE_LOG':
            return {
                ...state,
                logs: state.logs.filter(log => log._id !== payload),
                currentLog: state.currentLog?._id === payload 
                    ? null 
                    : state.currentLog,
                isLoading: false,
                error: null
            };

        // Filter-based logs
        case 'SET_STUDENT_LOGS':
            return {
                ...state,
                studentLogs: payload,
                isLoading: false,
                error: null
            };

        case 'SET_COURSE_LOGS':
            return {
                ...state,
                courseLogs: payload,
                isLoading: false,
                error: null
            };

        case 'SET_LAB_LOGS':
            return {
                ...state,
                labLogs: payload,
                isLoading: false,
                error: null
            };

        case 'SET_PROTOCOL_LOGS':
            return {
                ...state,
                protocolLogs: payload,
                isLoading: false,
                error: null
            };

        // Device Management
        case 'SET_DEVICES':
            return {
                ...state,
                devices: payload,
                isLoading: false,
                error: null
            };

        case 'SET_DEVICE_STATUS':
            return {
                ...state,
                deviceStatus: payload,
                isLoading: false,
                error: null
            };

        case 'UPDATE_DEVICE_IN_LIST':
            return {
                ...state,
                devices: state.devices.map(device =>
                    device.deviceId === payload.deviceId ? payload : device
                )
            };

        case 'REMOVE_DEVICE_FROM_LIST':
            return {
                ...state,
                devices: state.devices.filter(device => device.deviceId !== payload)
            };

        // Analytics
        case 'SET_ANALYTICS':
            return {
                ...state,
                analytics: payload,
                isLoading: false,
                error: null
            };

        case 'SET_LAB_ANALYTICS':
            return {
                ...state,
                labAnalytics: payload,
                isLoading: false,
                error: null
            };

        // Lab Sessions
        case 'SET_ACTIVE_SESSIONS':
            return {
                ...state,
                activeSessions: payload,
                isLoading: false,
                error: null
            };

        case 'SET_CURRENT_SESSION':
            return {
                ...state,
                currentSession: payload,
                isLoading: false,
                error: null
            };

        case 'ADD_SESSION':
            return {
                ...state,
                activeSessions: [payload, ...state.activeSessions],
                isLoading: false,
                error: null
            };

        case 'UPDATE_SESSION':
            return {
                ...state,
                activeSessions: state.activeSessions.map(session =>
                    session.sessionId === payload.sessionId ? payload : session
                ),
                currentSession: state.currentSession?.sessionId === payload.sessionId 
                    ? payload 
                    : state.currentSession,
                isLoading: false,
                error: null
            };

        case 'REMOVE_SESSION':
            return {
                ...state,
                activeSessions: state.activeSessions.filter(
                    session => session.sessionId !== payload
                ),
                currentSession: state.currentSession?.sessionId === payload 
                    ? null 
                    : state.currentSession,
                isLoading: false,
                error: null
            };

        // Pagination
        case 'SET_PAGINATION':
            return {
                ...state,
                pagination: payload
            };

        case 'UPDATE_PAGINATION':
            return {
                ...state,
                pagination: {
                    ...state.pagination,
                    ...payload
                }
            };

        // Log Status Updates
        case 'MARK_LOG_PROCESSED':
            return {
                ...state,
                logs: state.logs.map(log =>
                    log._id === payload ? { ...log, status: 'processed' } : log
                ),
                currentLog: state.currentLog?._id === payload 
                    ? { ...state.currentLog, status: 'processed' } 
                    : state.currentLog
            };

        case 'MARK_LOG_ERROR':
            return {
                ...state,
                logs: state.logs.map(log =>
                    log._id === payload.id 
                        ? { ...log, status: 'error', errorMessage: payload.message } 
                        : log
                ),
                currentLog: state.currentLog?._id === payload.id 
                    ? { ...state.currentLog, status: 'error', errorMessage: payload.message } 
                    : state.currentLog
            };

        case 'MARK_LOG_ARCHIVED':
            return {
                ...state,
                logs: state.logs.map(log =>
                    log._id === payload ? { ...log, status: 'archived' } : log
                ),
                currentLog: state.currentLog?._id === payload 
                    ? { ...state.currentLog, status: 'archived' } 
                    : state.currentLog
            };

        // Loading & Error
        case 'SET_LOADING':
            return {
                ...state,
                isLoading: payload
            };

        case 'SET_ERROR':
            return {
                ...state,
                error: payload,
                isLoading: false
            };

        case 'CLEAR_ERROR':
            return {
                ...state,
                error: null
            };

        // Clear specific data
        case 'CLEAR_CURRENT_LOG':
            return {
                ...state,
                currentLog: null
            };

        case 'CLEAR_CURRENT_SESSION':
            return {
                ...state,
                currentSession: null
            };

        case 'SET_AI_GENERATING':
            return {
                ...state,
                aiGeneration: {
                    ...state.aiGeneration,
                    isGenerating: payload,
                    generationError: payload ? null : state.aiGeneration.generationError
                }
            };

        case 'SET_GENERATED_RUBRICS':
            return {
                ...state,
                aiGeneration: {
                    ...state.aiGeneration,
                    generatedRubrics: payload,
                    isGenerating: false,
                    generationError: null
                }
            };

        case 'SET_COMPLETE_REQUIREMENTS':
            return {
                ...state,
                aiGeneration: {
                    ...state.aiGeneration,
                    completeRequirements: payload,
                    isGenerating: false,
                    generationError: null
                }
            };

        case 'SET_AI_GENERATION_ERROR':
            return {
                ...state,
                aiGeneration: {
                    ...state.aiGeneration,
                    isGenerating: false,
                    generationError: payload
                }
            };

        case 'CLEAR_AI_GENERATION':
            return {
                ...state,
                aiGeneration: {
                    generatedRubric: null,
                    completeRequirement: null,
                    isGenerating: false,
                    generationError: null
                }
            };

        case 'CLEAR_GENERATED_RUBRIC':
            return {
                ...state,
                aiGeneration: {
                    ...state.aiGeneration,
                    generatedRubric: null,
                    generationError: null
                }
            };

        case 'CLEAR_COMPLETE_REQUIREMENT':
            return {
                ...state,
                aiGeneration: {
                    ...state.aiGeneration,
                    completeRequirement: null,
                    generationError: null
                }
            };

        case 'CLEAR_IOT_DATA':
            return {
                logs: [],
                currentLog: null,
                studentLogs: [],
                courseLogs: [],
                labLogs: [],
                protocolLogs: [],
                devices: [],
                deviceStatus: null,
                analytics: null,
                labAnalytics: null,
                activeSessions: [],
                currentSession: null,
                pagination: {
                    total: 0,
                    count: 0,
                    limit: 100,
                    skip: 0,
                    hasMore: false
                },
                aiGeneration: {
                    generatedRubric: null,
                    completeRequirement: null,
                    isGenerating: false,
                    generationError: null
                },
                isLoading: false,
                error: null
            };


        default:
            return state;
    }
};

export default iotReducer;